import React from "react";
import { FatButton, FatButtonInputRightSideProp, FatButtonInputWrapper, LooseRadioInput } from "../__Internal__";
import { useEvent } from "../hooks/useEvent";
import { useRadioGroupState } from "./RadioGroupStateContext";

type Props = {
  /**
   * The value of the radio button. Is not selectable when `null`.
   */
  value: string | null;
  /**
   * The title text. Used as the label for the radio input.
   */
  title: React.ReactNode;
  /**
   * The subtitle text.
   * @default undefined
   */
  subtitle?: React.ReactNode;

  /**
   * The element to render on the right side of the radio button.
   * @default null
   */
  rightSide?: FatButtonInputRightSideProp;

  /**
   * An optional extension that appears under the checkbox when selected.
   * @default null
   */
  extension?: React.ReactNode;

  /**
   * The icon to render on the left side of the radio button.
   * @default null
   */
  icon?: string;

  /**
   * Whether the radio button is disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether the radio button is compact or has min-width.
   * @default false
   */
  compact?: boolean;

  onKeyPress?: React.KeyboardEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;

  /**
   * Internal do not use.
   * @private
   * @default undefined
   */
  UNSAFE_flatBottomRight?: boolean;

  /**
   * Testing utility tag.
   * @default "radio-button"
   */
  "data-testid"?: string;
};

const noopFn = () => null;

const propsAreEqual = (prevProps: Props, nextProps: Props) => {
  return (
    prevProps.value === nextProps.value &&
    prevProps.title === nextProps.title &&
    prevProps.subtitle === nextProps.subtitle &&
    prevProps.rightSide === nextProps.rightSide &&
    prevProps.icon === nextProps.icon &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.onKeyPress === nextProps.onKeyPress &&
    prevProps.onFocus === nextProps.onFocus &&
    prevProps.UNSAFE_flatBottomRight === nextProps.UNSAFE_flatBottomRight &&
    prevProps.extension === nextProps.extension
  );
};

export const RadioButton = React.memo(
  React.forwardRef<HTMLInputElement, Props>((props, forwardedRef) => {
    const {
      value,
      title,
      UNSAFE_flatBottomRight,
      extension = null,
      icon = null,
      subtitle = null,
      rightSide = null,
      onKeyPress = noopFn,
      onFocus = noopFn,
      compact = false,
    } = props;
    const testId = props["data-testid"] ?? "radio-button";
    const state = useRadioGroupState();
    const { name, selected, horizontal, required, invalid, onInvalid } = state;
    const disabled = (props.disabled || state.disabled) ?? false;

    let checked = false;
    let inputRequired = false;
    let id: undefined | string;

    /**
     * `value` can be `null` in cases where the rightSide is an overflow menu and we have not selected an input value yet.
     * No assigning these attributes prevents weird tabindex behaviour of the input.
     */
    if (value !== null) {
      checked = selected === value;
      id = `${name}-${value}`;
      inputRequired = required;
    }

    /**
     * We do this to prevent the input from stealing the selected value.
     * Without this, clicking the input will trigger a change event and set the value of the group to `undefined`
     * however we want to maintain the previously selected value. The `value` prop can be `null` in cases where
     * there is an overflow menu but no default value has been selected.
     */
    const handleChange = useEvent<React.ChangeEventHandler<HTMLInputElement>>((ev) => {
      if (value === null) {
        ev.preventDefault();
        return;
      }

      state.onChange(ev);
    });

    return (
      <FatButton.BaseContainer $horizontal={horizontal}>
        <FatButtonInputWrapper
          ref={forwardedRef}
          id={id}
          checked={checked}
          horizontal={horizontal}
          disabled={disabled}
          invalid={invalid}
          value={value}
          title={title}
          subtitle={subtitle}
          rightSide={rightSide}
          extension={extension}
          icon={icon}
          compact={compact}
          {...(UNSAFE_flatBottomRight ? { flatBottomRight: UNSAFE_flatBottomRight } : {})}
        >
          {(childProps) => (
            <LooseRadioInput
              {...childProps}
              state={state.radio}
              name={name}
              required={inputRequired}
              onKeyPress={onKeyPress}
              onFocus={onFocus}
              onInvalid={onInvalid}
              onChange={handleChange}
              data-testid={testId}
            />
          )}
        </FatButtonInputWrapper>
      </FatButton.BaseContainer>
    );
  }),
  propsAreEqual
);

RadioButton.displayName = "RadioButton";
